import { X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { DraftRecoveryDialog } from '@/components/common/draft-recovery-dialog'
import { DraftSavedIndicator } from '@/components/common/draft-saved-indicator'
import { DateQuickSelect } from '@/components/ui/date-quick-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useEnhancedDraft } from '@/hooks'
import {
  useCreateLeave,
  useMyLeaveBalance,
  type LeaveType,
  type CreateLeaveInput,
  type LeaveBalance,
} from '@/hooks'
import { cn } from '@/lib/utils'

interface LeaveFormProps {
  onClose: () => void
  onSuccess?: () => void
}

interface LeaveDraftData {
  type: LeaveType
  startDate: string
  endDate: string
  reason: string
}

const TYPE_OPTIONS: { value: LeaveType; label: string }[] = [
  { value: 'annual', label: '年假' },
  { value: 'sick', label: '病假' },
  { value: 'personal', label: '事假' },
  { value: 'marriage', label: '婚假' },
  { value: 'maternity', label: '产假' },
  { value: 'paternity', label: '陪产假' },
  { value: 'bereavement', label: '丧假' },
]

const initialDraftData: LeaveDraftData = {
  type: 'annual',
  startDate: '',
  endDate: '',
  reason: '',
}

/**
 * 请假表单组件
 * 支持草稿保存与恢复
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5
 */
export function LeaveForm({ onClose, onSuccess }: LeaveFormProps) {
  const createMutation = useCreateLeave()
  const { data: balances } = useMyLeaveBalance()

  // 草稿功能
  const {
    data: draftData,
    setData: setDraftData,
    hasRestoredDraft,
    clearDraft,
    lastSavedAt,
  } = useEnhancedDraft<LeaveDraftData>({
    key: 'leave_form',
    initialValue: initialDraftData,
    autoSaveDelay: 1000,
  })

  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // 检查是否有草稿需要恢复
  useEffect(() => {
    if (hasRestoredDraft && lastSavedAt) {
      setShowRecoveryDialog(true)
    }
  }, [hasRestoredDraft, lastSavedAt])

  // 更新表单数据
  const updateFormData = (updates: Partial<LeaveDraftData>) => {
    setDraftData((prev) => ({ ...prev, ...updates }))
  }

  // 恢复草稿
  const handleRecoverDraft = () => {
    // 草稿数据已经在 draftData 中，无需额外操作
    setShowRecoveryDialog(false)
  }

  // 丢弃草稿
  const handleDiscardDraft = () => {
    clearDraft()
    setDraftData(initialDraftData)
    setShowRecoveryDialog(false)
  }

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!draftData.startDate) {newErrors.startDate = '请选择开始日期'}
    if (!draftData.endDate) {newErrors.endDate = '请选择结束日期'}
    if (draftData.startDate && draftData.endDate && draftData.startDate > draftData.endDate) {
      newErrors.endDate = '结束日期不能早于开始日期'
    }
    if (!draftData.reason.trim()) {newErrors.reason = '请输入请假原因'}
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent, submit: boolean) => {
    e.preventDefault()
    if (!validate()) {return}

    const data: CreateLeaveInput = {
      type: draftData.type,
      startDate: draftData.startDate,
      endDate: draftData.endDate,
      reason: draftData.reason,
      submit,
    }
    createMutation.mutate(data, { 
      onSuccess: () => { 
        // 提交成功后清除草稿 (Requirements: 8.3)
        clearDraft()
        onSuccess?.()
        onClose() 
      } 
    })
  }

  const selectedBalance = balances?.find((b: LeaveBalance) => b.type === draftData.type)

  return (
    <>
      {/* 草稿恢复对话框 */}
      <DraftRecoveryDialog
        open={showRecoveryDialog}
        onOpenChange={setShowRecoveryDialog}
        savedAt={lastSavedAt?.getTime() ?? null}
        onRecover={handleRecoverDraft}
        onDiscard={handleDiscardDraft}
        formTypeName="请假申请"
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="w-full max-w-lg rounded-lg bg-background shadow-lg">
          <div className="flex items-center justify-between border-b px-6 py-4">
            <div>
              <h2 className="text-lg font-semibold">申请请假</h2>
              {/* 草稿已保存指示器 (Requirements: 8.4) */}
              <DraftSavedIndicator lastSavedAt={lastSavedAt?.getTime() ?? null} />
            </div>
            <button onClick={onClose} className="rounded-md p-1 hover:bg-muted">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form className="p-6">
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  请假类型 <span className="text-destructive">*</span>
                </label>
                <Select
                  value={draftData.type}
                  onValueChange={(value) => updateFormData({ type: value as LeaveType })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择请假类型" />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedBalance && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    剩余: {selectedBalance.remaining} 天 (已用 {selectedBalance.used}/{selectedBalance.total})
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    开始日期 <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="date"
                    value={draftData.startDate}
                    onChange={(e) => updateFormData({ startDate: e.target.value })}
                    className={cn(
                      'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                      errors.startDate && 'border-destructive'
                    )}
                  />
                  {/* 日期快捷选项 (Requirements: 11.6) */}
                  <DateQuickSelect
                    value={draftData.startDate}
                    onChange={(date) => updateFormData({ startDate: date })}
                    className="mt-2"
                    options={{
                      includeToday: true,
                      includeTomorrow: true,
                      includeNextMonday: true,
                    }}
                  />
                  {errors.startDate && <p className="mt-1 text-sm text-destructive">{errors.startDate}</p>}
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    结束日期 <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="date"
                    value={draftData.endDate}
                    onChange={(e) => updateFormData({ endDate: e.target.value })}
                    className={cn(
                      'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                      errors.endDate && 'border-destructive'
                    )}
                  />
                  {/* 日期快捷选项 (Requirements: 11.6) */}
                  <DateQuickSelect
                    value={draftData.endDate}
                    onChange={(date) => updateFormData({ endDate: date })}
                    className="mt-2"
                    options={{
                      includeToday: true,
                      includeTomorrow: true,
                      includeNextMonday: true,
                      includeThisWeekEnd: true,
                    }}
                  />
                  {errors.endDate && <p className="mt-1 text-sm text-destructive">{errors.endDate}</p>}
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  请假原因 <span className="text-destructive">*</span>
                </label>
                <textarea
                  value={draftData.reason}
                  onChange={(e) => updateFormData({ reason: e.target.value })}
                  placeholder="请输入请假原因"
                  rows={3}
                  className={cn(
                    'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                    errors.reason && 'border-destructive'
                  )}
                />
                {errors.reason && <p className="mt-1 text-sm text-destructive">{errors.reason}</p>}
              </div>

              {createMutation.error && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {createMutation.error.message}
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium hover:bg-muted">
                取消
              </button>
              <button
                type="button"
                onClick={(e) => handleSubmit(e, false)}
                disabled={createMutation.isPending}
                className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
              >
                保存草稿
              </button>
              <button
                type="button"
                onClick={(e) => handleSubmit(e, true)}
                disabled={createMutation.isPending}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {createMutation.isPending ? '提交中...' : '提交申请'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}
